import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Clock, Mail, Palette, Type, User } from 'lucide-react';
import debounce from 'lodash/debounce';
import { authManager, FONT_SIZES } from '@/managers/AuthManager';
import { useTheme, themes } from '@/managers/ThemeProvider';

const FONT_SIZE_VALUES = ['xs', 'sm', 'md', 'lg', 'xl'];

export function ProfileSection({
    user,
    userTier,
    onTabChange,
    isOnFreeTrial,
    daysRemaining,
    profileLoading,
    firestoreProfileData
  }) {
    const [authState, setAuthState] = useState(authManager.getState());
    const [displayName, setDisplayName] = useState('');
    const { currentTheme, setPersistentTheme } = useTheme();

    useEffect(() => {
      const unsubscribe = authManager.subscribe(setAuthState);
      return unsubscribe;
    }, []);

    useEffect(() => {
      authManager.setCurrentTheme(currentTheme);
    }, [currentTheme]);

    useEffect(() => {
      const currentDisplayName = firestoreProfileData?.displayName || user?.displayName || '';
      const savedDisplayName = authState.settings?.zPreferences?.userMeta?.displayName || currentDisplayName;
      setDisplayName(savedDisplayName);
    }, [authState.settings, firestoreProfileData, user?.displayName]);

    const debouncedUpdateUserMeta = useMemo(
      () => debounce((metaUpdates) => authManager.updateUserMeta(metaUpdates), 600),
      []
    );

    useEffect(() => {
      return () => debouncedUpdateUserMeta.cancel();
    }, [debouncedUpdateUserMeta]);

    const handleNameChange = useCallback((event) => {
      const newName = event.target.value;
      setDisplayName(newName);
      debouncedUpdateUserMeta({ displayName: newName });
    }, [debouncedUpdateUserMeta]);

    const handleFontSizeChange = useCallback((type, event) => {
      const nextValue = FONT_SIZE_VALUES[Number(event.target.value)] || 'md';
      authManager.updateSettings({
        zPreferences: {
          [type === 'chat' ? 'chatFontSize' : 'codeFontSize']: nextValue
        }
      });
    }, []);

    if (authState.isAuthenticating || (user?.uid && profileLoading)) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-pulse text-primary">Loading profile...</div>
        </div>
      );
    }

    const settings = authState.settings;
    const chatFontSize = settings?.zPreferences?.chatFontSize || 'md';
    const codeFontSize = settings?.zPreferences?.codeFontSize || 'md';
    const planLabel = userTier ? userTier.charAt(0).toUpperCase() + userTier.slice(1) : 'Free';
    const accountProvider = user?.providerData?.[0]?.providerId === 'google.com' ? 'Google' : 'Email';

    return (
      <div className="space-y-6">
        <div className="bg-surface-raised p-6 rounded-xl border border-border-subtle">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-primary/10 text-primary rounded-full">
                <User className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-medium text-on-surface">Profile & Preferences</h1>
                <p className="text-sm text-on-surface-dim">Account identity, theme color, and default font sizes</p>
              </div>
            </div>

            <div className="bg-surface p-3 rounded-lg border border-border-subtle min-w-[220px]">
              <div className="text-xs text-on-surface-dim mb-1">Current Plan</div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-on-surface bg-primary px-2 py-0.5 rounded">
                    {planLabel}
                  </span>
                  {isOnFreeTrial && (
                    <span className="text-xs text-success bg-success/10 px-1.5 py-0.5 rounded">
                      Trial - {daysRemaining} days left
                    </span>
                  )}
                </div>

                {!isOnFreeTrial && userTier === 'Free' && (
                  <button
                    onClick={() => onTabChange('subscription')}
                    className="text-xs text-primary flex items-center flex-shrink-0"
                  >
                    Upgrade <ArrowRight className="w-3 h-3 ml-1" />
                  </button>
                )}
                {isOnFreeTrial && daysRemaining <= 7 && (
                  <button
                    onClick={() => onTabChange('subscription')}
                    className="text-xs text-primary flex items-center flex-shrink-0"
                  >
                    Continue <ArrowRight className="w-3 h-3 ml-1" />
                  </button>
                )}
              </div>

              {isOnFreeTrial && daysRemaining <= 3 && (
                <div className="mt-2 pt-2 border-t border-border-subtle">
                  <div className="flex items-center gap-2 text-xs text-warning font-medium">
                    <Clock className="w-3 h-3" />
                    Trial ending soon.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-on-surface mb-1.5">
                What should your bots call you?
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={handleNameChange}
                placeholder="Enter your preferred name"
                className="w-full px-3 py-2.5 bg-surface border border-border-color rounded-lg text-on-surface placeholder:text-on-surface-dim/50 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <div className="text-sm font-medium text-on-surface mb-1.5">
                Connected Account
              </div>
              <div className="flex items-center bg-surface px-3 py-2.5 border border-border-color rounded-lg">
                <div className={`w-2 h-2 rounded-full ${user?.emailVerified ? 'bg-success' : 'bg-warning'} mr-2`} />
                <Mail className="w-4 h-4 text-on-surface-dim mr-2" />
                <span className="text-on-surface text-sm truncate">{user?.email}</span>
                <div className="ml-auto text-xs px-2 py-0.5 bg-surface-dark/50 rounded text-on-surface-dim">
                  {accountProvider}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-raised p-6 rounded-xl border border-border-subtle space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-primary/10 text-primary rounded-full">
              <Palette className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-on-surface">Preferences</h2>
              <p className="text-sm text-on-surface-dim">Theme color and default font sizes</p>
            </div>
          </div>

          <div className="border border-border-subtle rounded-xl p-4 bg-surface/40">
            <div className="flex items-center gap-2 mb-3">
              <Palette className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-medium text-on-surface">Theme Color</h3>
            </div>
            <div className="flex flex-wrap gap-4">
              {Object.entries(themes).map(([themeId, theme]) => (
                <button
                  key={themeId}
                  type="button"
                  onClick={() => setPersistentTheme(themeId)}
                  className={`group flex items-center gap-3 rounded-full border px-3 py-2 transition-all ${
                    currentTheme === themeId
                      ? 'border-primary bg-primary/10'
                      : 'border-border-subtle bg-surface hover:border-primary/40'
                  }`}
                  title={theme.name}
                >
                  <span
                    className="block h-5 w-5 rounded-full border border-white/30"
                    style={{ backgroundColor: theme.color }}
                  />
                  <span className="text-xs text-on-surface-dim group-hover:text-on-surface">
                    {theme.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="border border-border-subtle rounded-xl p-4 bg-surface/40">
            <div className="flex items-center gap-2 mb-4">
              <Type className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-medium text-on-surface">Default Font Size</h3>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-on-surface-dim">Chat text</span>
                  <span className="text-primary">{FONT_SIZES[chatFontSize]}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="4"
                  step="1"
                  value={FONT_SIZE_VALUES.indexOf(chatFontSize)}
                  onChange={(event) => handleFontSizeChange('chat', event)}
                  className="w-full accent-primary"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-on-surface-dim">Code text</span>
                  <span className="text-primary">{FONT_SIZES[codeFontSize]}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="4"
                  step="1"
                  value={FONT_SIZE_VALUES.indexOf(codeFontSize)}
                  onChange={(event) => handleFontSizeChange('code', event)}
                  className="w-full accent-primary"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  export default ProfileSection;